import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { InnerPageLayout } from "./InnerPageLayout";
import { CASE_STUDIES } from "../data/pages";
import { Seo } from "../components/site/Seo";

const FILTERS = [
  "All Work",
  "AI & Intelligent Systems",
  "SaaS Products",
  "Data & Analytics",
  "Manufacturing",
  "E-commerce & Fulfillment",
  "Logistics",
  "IoT",
  "Workflow Automation",
];

const CATEGORY_MATCH = {
  "AI & Intelligent Systems": [
    "ai-sales-content-technology",
    "shipolog-ai-fulfillment",
  ],
  "SaaS Products": ["ai-sales-content-technology", "shipolog-ai-fulfillment"],
  "Data & Analytics": ["swiggy-payment-reconciliation", "iot-monitoring-operational-visibility"],
  Manufacturing: ["gallop-brush-erp"],
  "E-commerce & Fulfillment": ["shipolog-ai-fulfillment"],
  Logistics: ["logistics-freight-technology", "shipolog-ai-fulfillment"],
  IoT: ["iot-monitoring-operational-visibility"],
  "Workflow Automation": [
    "logistics-freight-technology",
    "gallop-brush-erp",
    "shipolog-ai-fulfillment",
    "swiggy-payment-reconciliation",
  ],
};

export const WorkIndexPage = () => {
  const [filter, setFilter] = useState("All Work");

  const cases = useMemo(() => {
    const all = Object.values(CASE_STUDIES);
    const filtered =
      filter === "All Work"
        ? all
        : all.filter((c) => new Set(CATEGORY_MATCH[filter] || []).has(c.slug));
    // Featured (e.g., unicorn client) surfaces first
    return [...filtered].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
  }, [filter]);

  return (
    <InnerPageLayout title="Our Work — Case Studies">
      <Seo
        title="Our Work — Case Studies"
        description="Explore how Techovarya approaches product challenges across AI, SaaS, operational platforms, data-intensive systems, workflow automation, and cloud engineering — including our work with Swiggy, Shipolog, and Gallop Brush."
        path="/work"
      />
      {/* Hero */}
      <section
        id="top"
        data-testid="work-hero"
        className="relative overflow-hidden bg-[#050505] pt-16 md:pt-24 pb-16 md:pb-20"
      >
        <div className="absolute inset-0 bg-grid opacity-40" aria-hidden="true" />
        <div className="absolute inset-0 radial-spotlight" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-zinc-500 mb-8">
            <Link to="/" className="hover:text-[#FF5F1F] transition-colors">
              Home
            </Link>
            <span className="text-zinc-700">/</span>
            <span>Work</span>
          </div>

          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-8 bg-[#FF5F1F]" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
              Our Work
            </span>
          </div>

          <h1
            data-testid="work-h1"
            className="font-headings text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-[-0.02em] leading-[1.02] text-white max-w-5xl"
          >
            Complex Products. <span className="text-zinc-500">Real Engineering Challenges.</span>
          </h1>

          <p className="mt-8 max-w-3xl text-base md:text-lg text-zinc-400 leading-relaxed">
            The strongest software work begins with context. Explore how we approach product
            challenges across AI, SaaS, operational platforms, data-intensive systems,
            integrations, workflow automation, mobile applications, and cloud engineering.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section
        data-testid="work-filters"
        className="sticky top-16 md:top-20 z-30 bg-[#050505]/90 backdrop-blur-xl border-y border-white/10"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex flex-wrap gap-2 md:gap-3">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`font-mono text-[10px] md:text-xs uppercase tracking-[0.15em] px-3 md:px-4 py-2 border transition-colors ${
                filter === f
                  ? "bg-[#FF5F1F] text-black border-[#FF5F1F]"
                  : "border-white/10 text-zinc-400 hover:border-[#FF5F1F]/50 hover:text-white"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section
        data-testid="work-grid"
        className="relative bg-[#050505] py-16 md:py-20"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {cases.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-zinc-500">No case studies match this filter yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {cases.map((c, i) => (
                <motion.div
                  key={c.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
                >
                  <Link
                    to={`/work/${c.slug}`}
                    data-testid={`work-card-${c.slug}`}
                    className="group relative overflow-hidden border border-white/10 bg-black min-h-[420px] flex flex-col"
                  >
                    <div className="absolute inset-0">
                      <img
                        src={c.image}
                        alt={c.h1}
                        className="w-full h-full object-cover opacity-40 group-hover:opacity-70 grayscale group-hover:grayscale-0 transition-all duration-700"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/10" />
                    </div>

                    <div className="relative z-10 mt-auto p-6 md:p-8 flex flex-col gap-4">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F]">
                          {String(i + 1).padStart(2, "0")} / {c.industry}
                        </span>
                        <div className="flex items-center gap-2">
                          {c.badge && (
                            <span
                              data-testid={`work-badge-${c.slug}`}
                              className="inline-flex items-center gap-1 bg-[#FF5F1F] text-black font-mono text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-1"
                            >
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 2l2.4 4.9L20 8l-4 3.9.9 5.5L12 14.8 7.1 17.4 8 11.9 4 8l5.6-1.1L12 2z"/></svg>
                              {c.badge}
                            </span>
                          )}
                          <ArrowUpRight className="w-5 h-5 text-white/60 group-hover:text-[#FF5F1F] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                        </div>
                      </div>
                      <h3 className="font-headings text-2xl md:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight max-w-xl">
                        {c.h1}
                      </h3>
                      <p className="text-sm md:text-base text-zinc-300 leading-relaxed max-w-lg">
                        {c.summary}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {c.capabilities.slice(0, 3).map((cap, ci) => (
                          <span
                            key={ci}
                            className="font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border border-white/15 text-zinc-300"
                          >
                            {cap}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </InnerPageLayout>
  );
};
